import { track, consent, CONSENT_EVENT } from '@verfi/shared/dist/analytics/browser';
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Player from './Player';
import axios from 'axios';
import '../styles/ProofStatus.css';
import LockedBadge from './LockedBadge';
import SmartDisplayOutlined from '@mui/icons-material/SmartDisplayOutlined';
import NativeProof from './NativeProof';
import MetadataPanel from './MetadataPanel';
import ProofHeader from './ProofHeader';
import UnlockModal from './UnlockModal';
import { apiService, Session, SessionEvent, NativeProofData, ProofAccess, WebProofEvidence } from '../services/api';

interface PlayerPageProps {
  verfiID?: string;
}

const PlayerPage: React.FC<PlayerPageProps> = ({ verfiID: propVerfiID }) => {
  const { verfiID: paramVerfiID } = useParams<{ verfiID: string }>();
  const verfiID = propVerfiID || paramVerfiID;
  
  const [session, setSession] = useState<Session | null>(null);
  const reportedLoad = useRef<string | null>(null);
  useEffect(() => {
    // Direct visitors often choose consent after the proof has already loaded.
    // Report the current successful load then, without sending the proof ID.
    if (!session?.verfiID) return;
    const report = () => {
      if (consent() === 'accepted' && reportedLoad.current !== session.verfiID) {
        track('proof_loaded'); reportedLoad.current = session.verfiID;
      }
    };
    report(); window.addEventListener(CONSENT_EVENT, report);
    return () => window.removeEventListener(CONSENT_EVENT, report);
  }, [session?.verfiID]);
  const [events, setEvents] = useState<SessionEvent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<'not-found' | 'unavailable' | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isUnlocked, setIsUnlocked] = useState<boolean>(false);
  
  const [ipAccess, setIPAccess] = useState<{ id: string; value: string | null; expiresAt: number } | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);
  const grantRef = useRef<ProofAccess | null>(null);
  const currentID = useRef(verfiID);
  currentID.current = verfiID;
  useEffect(() => {
    if (!ipAccess) return;
    const timer = window.setTimeout(() => {
      setIPAccess(null); setIsUnlocked(false); grantRef.current = null;
      setEvents([]); setSession({ verfiID: ipAccess.id } as Session);
      localStorage.removeItem(`verfi_proof_access_${ipAccess.id}`);
      localStorage.removeItem(`verfi_unlocked_${ipAccess.id}`);
      localStorage.removeItem(`verfi_unlock_timestamp_${ipAccess.id}`);
    }, Math.max(0, ipAccess.expiresAt - Date.now()));
    return () => window.clearTimeout(timer);
  }, [ipAccess]);

  useEffect(() => {
    const changed = (event: StorageEvent) => {
      if (event.key === `verfi_proof_access_${verfiID}` && event.newValue === null) {
        grantRef.current = null; setIPAccess(null); setIsUnlocked(false);
        setEvents([]); setSession({ verfiID } as Session);
      }
    };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, [verfiID]);

  // Fetch session data and events
  useEffect(() => {
    let cancelled = false;
    setIPAccess(null); setAccessError(null); grantRef.current = null;
    setSession(null); setError(null); setIsUnlocked(false); setEvents([]);
    const fetchSessionData = async () => {
      if (!verfiID) {
        setError('not-found');
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        console.log('Fetching session data for:', verfiID);
        
        // Fetch session details
        const sessionData = await apiService.getSession(verfiID);
        if (cancelled) return;
        setSession(sessionData);
        if (sessionData.evidenceType === 'native_submission') { setIsLoading(false); return; }
        
        // Restore only a server-issued proof-scoped grant, never a UI boolean.
        const key = `verfi_proof_access_${verfiID}`;
        const saved = localStorage.getItem(key);
        localStorage.removeItem(`verfi_unlocked_${verfiID}`);
        localStorage.removeItem(`verfi_unlock_timestamp_${verfiID}`);
        if (saved) {
          let grant: ProofAccess | null = null;
          try { grant = JSON.parse(saved); } catch (_error) { localStorage.removeItem(key); }
          if (grant?.token && +new Date(grant.expiresAt) > Date.now()) {
            try {
              const restored = await apiService.restoreAccess(verfiID, grant.token);
              if (cancelled) return;
              if (restored.session?.verfiID !== verfiID || !Array.isArray(restored.events)) throw new Error('Invalid access response');
              setSession(restored.session); setEvents(restored.events);
              grantRef.current = { token: grant.token, expiresAt: restored.expiresAt };
              setIPAccess({ id: verfiID, value: restored.reportedIP, expiresAt: +new Date(restored.expiresAt) });
              setIsUnlocked(true);
              localStorage.setItem(`verfi_unlock_timestamp_${verfiID}`, String(+new Date(restored.expiresAt) - 86400000));
            } catch (err: any) {
              if (cancelled) return;
              if ([401, 404, 410].includes(err.response?.status)) localStorage.removeItem(key);
              else throw err; // Temporary network failures must not silently destroy access.
            }
          } else localStorage.removeItem(key);
        }

        setIsLoading(false);
      } catch (err) {
        if (cancelled) return;
        track('operation_failed', { operation: 'load', outcome: 'failure' });
        console.error('Error fetching session data');
        setError(axios.isAxiosError(err) && err.response?.status === 404 ? 'not-found' : 'unavailable');
        setIsLoading(false);
      }
    };
    
    fetchSessionData();
    return () => { cancelled = true; };
  }, [verfiID, retryCount]);
  
  if (isLoading) {
    return (
      <div className="proof-layout">
        <ProofHeader session={null} isUnlocked={false} />
        <MetadataPanel session={null} loading={true} error={null} />
        <div className="player-section">
          <div className="loading-container">
            <div className="loading-spinner">Loading player...</div>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="proof-layout">
        <ProofHeader session={null} isUnlocked={false} />
        <main className="proof-status">
          <div className="proof-status-card">
            <SmartDisplayOutlined className="proof-status-icon" aria-hidden="true" />
            <h1>{error === 'not-found' ? 'Proof not found' : 'Unable to load this proof'}</h1>
            {error === 'not-found' ? <>
              <p>We couldn’t find a proof matching this link.</p>
              {verfiID && <div className="proof-status-id">{verfiID}</div>}
              <p>Check that you copied the full link, or ask the person who shared it to send it again.</p>
            </> : <>
              <p>We couldn’t load this proof right now. Please try again in a moment.</p>
              <button className="proof-status-retry" onClick={() => setRetryCount(count => count + 1)}>Try again</button>
            </>}
          </div>
        </main>
      </div>
    );
  }
  
  if (session?.evidenceType === 'native_submission') return <NativeProof key={session.verfiID} session={session} />;

  const handleUnlock = (_proof?: NativeProofData, reportedIP?: string | null, access?: ProofAccess, evidence?: WebProofEvidence) => {
    if (currentID.current !== verfiID || !verfiID) return;
    if (!access || reportedIP === undefined || evidence?.session.verfiID !== verfiID) {
      setAccessError('Unable to unlock this proof. Please try again.');
      return;
    }
    setSession(evidence.session); setEvents(evidence.events);
    grantRef.current = access;
    localStorage.setItem(`verfi_proof_access_${verfiID}`, JSON.stringify(access));
    localStorage.setItem(`verfi_unlock_timestamp_${verfiID}`, String(+new Date(access.expiresAt) - 86400000));
    setIPAccess({ id: verfiID, value: reportedIP, expiresAt: +new Date(access.expiresAt) });
    setAccessError(null); setIsUnlocked(true);
  };

  const handleEndAccess = async () => {
    if (!verfiID) return;
    try {
      if (grantRef.current) await apiService.endAccess(verfiID, grantRef.current.token);
      if (currentID.current !== verfiID) return;
      grantRef.current = null; setIPAccess(null); setIsUnlocked(false);
      setEvents([]); setSession({ verfiID } as Session); setAccessError(null);
      localStorage.removeItem(`verfi_proof_access_${verfiID}`);
      localStorage.removeItem(`verfi_unlocked_${verfiID}`);
      localStorage.removeItem(`verfi_unlock_timestamp_${verfiID}`);
    } catch (_error) { setAccessError('Unable to end access. Please try again.'); }
  };

  return (
    <div className="proof-layout">
      <ProofHeader session={session} isUnlocked={isUnlocked} onEndAccess={handleEndAccess} />
      {accessError && <div role="alert">{accessError}</div>}
      {/* Metadata Panel on Top */}
      <MetadataPanel session={session} loading={false} error={null} isUnlocked={isUnlocked}
        reportedIP={isUnlocked && ipAccess?.id === verfiID ? ipAccess?.value : undefined}
        />
      
      {/* Player Section on Bottom */}
      <div className="player-section">
        {isUnlocked ? <Player events={events} height="100%" /> : <div className="locked-replay">
          <div className="locked-replay-heading"><span>Session replay</span><LockedBadge /></div>
          <div className="locked-replay-screen">
            <SmartDisplayOutlined className="locked-replay-icon" aria-hidden="true" />
          </div>
          <div className="locked-replay-controls" aria-hidden="true">
            <span className="locked-replay-play">▶</span><span className="locked-replay-track" />
            <span>Unlock to watch</span>
          </div>
        </div>}
      </div>
      
      {/* Unlock Modal - Show for all sessions unless unlocked (PII protection) */}
      {!isUnlocked && (
        <UnlockModal key={verfiID}
          verfiID={verfiID!} 
          session={session}
          onUnlock={handleUnlock}
        />
      )}
    </div>
  );
};

export default PlayerPage;
